import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../context/RoleContext';
import { api } from '../api/client';
import { TabBar } from '../components/TabBar';
import type { PermissionCapability } from '../types';
import { useT } from '../i18n';

export function ProfilePage() {
  const { t } = useT();
  const [tab, setTab] = useState('account');

  const TABS = [
    { key: 'account', label: t('account.tab.account') },
    { key: 'permissions', label: t('account.tab.permissions') },
    { key: 'notifications', label: t('account.tab.notifications') },
  ];

  return (
    <div style={{ maxWidth: 640 }}>
      <TabBar tabs={TABS} activeTab={tab} onTabChange={setTab} />

      {tab === 'account' && <AccountTab />}
      {tab === 'permissions' && <PermissionsTab />}
      {tab === 'notifications' && <NotificationsTab />}
    </div>
  );
}

function AccountTab() {
  const { t } = useT();
  const { user, authMethod } = useAuth();
  const { role, staffId } = useRole();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwStatus, setPwStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [pwError, setPwError] = useState('');

  const handlePasswordChange = async () => {
    setPwError('');
    if (newPw.length < 8) { setPwError(t('account.pwd.errMinLength')); return; }
    if (newPw !== confirmPw) { setPwError(t('account.pwd.errMismatch')); return; }
    setPwStatus('saving');
    try {
      await api.changePassword(currentPw, newPw);
      setPwStatus('saved');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => setPwStatus('idle'), 2000);
    } catch (err: any) {
      setPwStatus('error');
      setPwError(err.message || t('account.pwd.errFailed'));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s24)' }}>
      <section>
        <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('account.tab.account')}</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s8)' }}>
          <div className="detail-row"><span className="detail-row-label">{t('account.field.name')}</span><span>{user?.name}</span></div>
          <div className="detail-row"><span className="detail-row-label">{t('account.field.email')}</span><span>{user?.email}</span></div>
          <div className="detail-row">
            <span className="detail-row-label">{t('account.field.role')}</span>
            <span className={`badge ${role === 'admin' ? 'badge-accent' : role.startsWith('resp_') ? 'badge-direction' : ''}`}>{role === 'resp_all' ? t('account.role.respAll') : role === 'resp_oai' ? t('account.role.respOai') : role === 'resp_ri' ? t('account.role.respRi') : role === 'utilisateur' ? t('account.role.utilisateur') : role.toUpperCase()}</span>
          </div>
          <div className="detail-row">
            <span className="detail-row-label">{t('account.field.staffId')}</span>
            <span>{staffId ?? t('common.notMapped')}</span>
          </div>
          <div className="detail-row">
            <span className="detail-row-label">{t('account.field.auth')}</span>
            <span className="text-ink-3" style={{ fontSize: 'var(--t-13)' }}>
              {authMethod === 'oidc' ? t('account.auth.sso') : t('account.auth.password')}
            </span>
          </div>
        </div>
      </section>

      {authMethod === 'password' && (
        <section>
          <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('account.section.changePassword')}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)' }}>
            <div className="form-group">
              <label className="form-label">{t('account.pwd.current')}</label>
              <input className="form-input" type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} style={{ maxWidth: 320 }} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('account.pwd.new')}</label>
              <input className="form-input" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder={t('account.pwd.minLengthHint')} style={{ maxWidth: 320 }} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('account.pwd.confirm')}</label>
              <input className="form-input" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} style={{ maxWidth: 320 }} />
            </div>
            {pwError && <div style={{ color: 'var(--err)', fontSize: 'var(--t-13)' }}>{pwError}</div>}
            <div>
              <button
                className="btn btn-accent btn-sm"
                onClick={handlePasswordChange}
                disabled={!currentPw || !newPw || !confirmPw || pwStatus === 'saving'}
              >
                {pwStatus === 'saving' ? t('common.saving') : pwStatus === 'saved' ? t('account.pwd.changed') : t('account.section.changePassword')}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function PermissionsTab() {
  const { t } = useT();
  const { role, isAdmin } = useRole();
  const [capabilities, setCapabilities] = useState<PermissionCapability[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (api.getPermissionsOverview() as Promise<{ capabilities: PermissionCapability[] }>)
      .then(data => setCapabilities(data.capabilities))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const renderCell = (value: boolean | string) => {
    if (value === true) return <span style={{ color: 'var(--ok)' }}>{t('common.yes')}</span>;
    if (value === false) return <span style={{ color: 'var(--ink-3)' }}>{t('common.no')}</span>;
    return <span style={{ color: 'var(--warn)', fontSize: 'var(--t-12)' }}>{value}</span>;
  };

  if (loading) return <p className="text-ink-3">{t('common.loading')}</p>;

  return (
    <div>
      <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s12)' }}>{t('account.tab.permissions')}</h2>
      <p className="text-sm text-ink-3" style={{ marginBottom: 'var(--s12)' }}>
        {t('account.perm.description', { role })}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table" style={{ width: '100%', fontSize: 'var(--t-13)' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{t('account.perm.capability')}</th>
              {isAdmin && <th>{t('account.perm.admin')}</th>}
              <th>{t('account.perm.respAll')}</th>
              <th>{t('account.perm.respOai')}</th>
              <th>{t('account.perm.respRi')}</th>
              <th>{t('account.perm.utilisateur')}</th>
            </tr>
          </thead>
          <tbody>
            {capabilities.map((cap, i) => (
              <tr key={i}>
                <td>{cap.name}</td>
                {isAdmin && <td style={{ textAlign: 'center' }}>{renderCell(cap.admin)}</td>}
                <td style={{ textAlign: 'center' }}>{renderCell(cap.resp_all)}</td>
                <td style={{ textAlign: 'center' }}>{renderCell(cap.resp_oai)}</td>
                <td style={{ textAlign: 'center' }}>{renderCell(cap.resp_ri)}</td>
                <td style={{ textAlign: 'center' }}>{renderCell(cap.utilisateur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface PrefRow { notification_type: string; enabled: boolean; label: string; description: string }
interface EventMeta { category: string; label: string; description: string }
interface CategoryHeader { key: string; label: string }

function NotificationsTab() {
  const { t } = useT();
  const [prefs, setPrefs] = useState<PrefRow[]>([]);
  const [meta, setMeta] = useState<{ events: Record<string, EventMeta>; categories: CategoryHeader[] } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.getNotificationPreferences() as Promise<PrefRow[]>,
      api.getNotificationMeta() as Promise<{ events: Record<string, EventMeta>; categories: CategoryHeader[] }>,
    ])
      .then(([p, m]) => { setPrefs(p); setMeta(m); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const togglePref = async (type: string, enabled: boolean) => {
    setPrefs(prev => prev.map(p => p.notification_type === type ? { ...p, enabled } : p));
    try {
      await api.updateNotificationPreference(type, enabled);
    } catch (err) {
      setPrefs(prev => prev.map(p => p.notification_type === type ? { ...p, enabled: !enabled } : p));
      throw err;
    }
  };

  if (loading) return <p className="text-ink-3">{t('common.loading')}</p>;

  // Group prefs by category. Unknown categories fall into "Autre" at the bottom.
  const byCategory = new Map<string, PrefRow[]>();
  for (const p of prefs) {
    const cat = meta?.events[p.notification_type]?.category ?? 'autre';
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(p);
  }
  const orderedCats: CategoryHeader[] = [
    ...(meta?.categories ?? []),
    ...(byCategory.has('autre') ? [{ key: 'autre', label: t('account.notif.categoryOther') }] : []),
  ];

  return (
    <div>
      <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 700, marginBottom: 'var(--s4)' }}>{t('account.notif.title')}</h2>
      <p className="text-ink-3" style={{ fontSize: 'var(--t-12)', marginBottom: 'var(--s16)' }}>
        {t('account.notif.description')}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s20)' }}>
        {orderedCats.map(cat => {
          const rows = byCategory.get(cat.key);
          if (!rows || rows.length === 0) return null;
          return (
            <section key={cat.key}>
              <h3 style={{
                fontSize: 'var(--t-11)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--ink-3)',
                marginBottom: 'var(--s8)',
              }}>{cat.label}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s10)' }}>
                {rows.map(p => (
                  <label key={p.notification_type} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--s8)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={p.enabled}
                      onChange={e => togglePref(p.notification_type, e.target.checked)}
                      style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--accent)' }}
                    />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 'var(--t-14)' }}>{p.label}</div>
                      <div className="text-ink-3" style={{ fontSize: 'var(--t-12)' }}>{p.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
